import cx from 'classnames';
import * as React from 'react';

import ClickEventTrigger from 'components/ClickEventTrigger';
import getPropClasses from 'lib/getPropClasses';
import LinkBase from 'components/LinkBase';
import LoadingIndicator, { LoadingIndicatorSize } from 'components/LoadingIndicator';
import tooltipable from 'lib/tooltipable';
import userAgent from 'lib/currentUserAgent';

import './styles/Button.scss';

import { Props as LinkBaseProps } from 'components/LinkBase';

/**
 * This is the most up-to-date Button component, but is missing many of the
 * styling options that the design team has created. Please feel free to add
 * them if you need them for your feature.
 *
 * Note that this new component uses `ButtonNew` for its CSS classes because we
 * have some poorly architected CSS in our app that directly references the `Button`
 * class and I wanted to avoid hunting those down and causing visual regressions.
 * So `ButtonDeprecated` uses `.Button` for CSS and `Button` uses `.ButtonNew` for CSS.
 *
 * https://www.figma.com/file/eGhfCfQjEtlvVAtcId98dA/RDL-Web?node-id=1608%3A61
 */

const propsToClasses = {
  size: {
    small: 'ButtonNew--small',
    long: 'ButtonNew--long',
  },
  fontSize: {
    xsmall: 'ButtonNew--font-xsmall',
    xxxsmall: 'ButtonNew--font-xxxsmall',
  },
  full: 'ButtonNew--full',
};

export type ButtonSize = 'medium' | 'small' | 'long';
export type ButtonFontSize = 'xxxsmall' | 'xxsmall';
export type ButtonColor = 'mascot' | 'whiteout' | 'koolaid' | 'apple' | 'paper';
export type ButtonLevel = 'primary' | 'secondary' | 'dashed' | 'composer-icon';

export type Props = LinkBaseProps & {
  'aria-label'?: string;
  'data-test'?: string;
  'data-testid'?: string;
  children?: React.ReactNode;
  color?: ButtonColor;
  eventContextName?: string;
  eventElementName?: string;
  eventKey?: string;
  eventProperties?: any;
  eventScreenName?: string;
  full?: boolean;
  id?: string;
  level?: ButtonLevel;
  loading?: boolean;
  size?: ButtonSize;
  fontSize?: ButtonFontSize;
  fullAtBreakpoint?: '1200' | '1024' | '480';
  type?: 'button' | 'submit' | 'reset' | undefined;
  loadingIndicatorSize?: LoadingIndicatorSize;
};

// Refrain from making this fancier with TypeScript,
// e.g. Pick<Props, 'full' | ... > as it will f-up
// yarn codegen-flow src/components/Button.tsx
type DefaultProps = {
  color: ButtonColor;
  disabled: boolean;
  download: boolean;
  full: boolean;
  level: ButtonLevel;
  loading: boolean;
  size: ButtonSize;
  fontSize: ButtonFontSize;
  loadingIndicationSize: LoadingIndicatorSize;
};

class Button extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    ...LinkBase.defaultProps,
    full: false,
    level: 'secondary',
    loading: false,
    size: 'medium',
    fontSize: 'xxxsmall',
    color: 'whiteout',
    loadingIndicationSize: 'small',
  };

  isDisabled = () => {
    return this.props.disabled || this.props.loading;
  };

  renderChildren = () => {
    if (this.props.loading) {
      const loadingIndicatorColor = this.props.color === 'mascot' ? 'whiteout' : 'percussion';
      return (
        <LoadingIndicator size={this.props.loadingIndicatorSize} color={loadingIndicatorColor} />
      );
    } else {
      return this.props.children;
    }
  };

  render() {
    const { rel, target, level, color, fontSize, fullAtBreakpoint } = this.props;
    const hasTarget = this.props.to || this.props.href;
    const Component = hasTarget ? LinkBase : 'button';
    const targetBlankRel = (() => {
      const baseRel = 'noopener';
      if (!userAgent.noreferrerSupported()) {
        return baseRel + ' noreferrer';
      }
      return baseRel;
    })();

    const safeRel = (() => {
      if (target === '_blank') {
        if (rel) {
          return `${rel} ${targetBlankRel}`;
        }
        return targetBlankRel;
      }
      return undefined;
    })();

    return (
      <ClickEventTrigger
        eventElementName={this.props.eventElementName}
        eventKey={this.props.eventKey}
        eventProperties={this.props.eventProperties}
        eventScreenName={this.props.eventScreenName}
      >
        <Component
          id={this.props.id}
          type={this.props.type}
          rel={safeRel}
          target={this.props.target}
          href={this.props.href}
          to={this.props.to}
          aria-label={this.props['aria-label']}
          download={this.props.download}
          onMouseEnter={this.props.onMouseEnter}
          onMouseLeave={this.props.onMouseLeave}
          onMouseMove={this.props.onMouseMove}
          data-test-selector={this.props['data-test-selector']}
          data-test={this.props['data-test']}
          data-testid={this.props['data-testid']}
          onClick={this.props.onClick}
          disabled={this.isDisabled()}
          className={cx(
            'ButtonNew',
            `ButtonNew--${level}--${color}`,
            `ButtonNew--font--${fontSize}`,
            `ButtonNew--${color}`,
            fullAtBreakpoint && `ButtonNew--full--${fullAtBreakpoint}`,
            this.props.className,
            { loading: this.props.loading },
            {
              'ButtonNew--disabled': this.isDisabled(),
            },
            getPropClasses(this.props, propsToClasses)
          )}
        >
          {this.renderChildren() || null}
        </Component>
      </ClickEventTrigger>
    );
  }
}

export { Button };
export default tooltipable(Button);
